import {
  isStaticSearchIndex,
  searchStaticIndex,
  type SearchFacets,
  type StaticSearchIndex,
  type StaticSearchResult,
} from "./search-core";

const CACHE_DATABASE = "klasklaar-search-cache";
const CACHE_STORE = "indexes";

const SEARCH_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <circle cx="11" cy="11" r="7"></circle>
    <path d="m20 20-3.6-3.6"></path>
  </svg>`;

const ARROW_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M7 17 17 7"></path>
    <path d="M7 7h10v10"></path>
  </svg>`;

const FILE_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
    <path d="M14 2v6h6"></path>
    <path d="M12 18v-6"></path>
    <path d="m9 15 3 3 3-3"></path>
  </svg>`;

const STYLES = `
  :host {
    --kk-text: #17232c;
    --kk-muted: #64747c;
    --kk-primary: #1677a6;
    --kk-border: #d6e3e8;
    display: block;
    width: 100%;
    color: var(--kk-text);
    font-family: "Trebuchet MS", "Avenir Next", Arial, sans-serif;
  }

  * { box-sizing: border-box; }
  [hidden] { display: none !important; }
  button, input, select { font: inherit; }

  .kk-shell {
    width: 100%;
    margin: 0;
    padding: 8px 0 24px;
  }

  .kk-form { margin: 0; }
  .kk-input-wrap { position: relative; }

  .kk-input {
    display: block;
    width: 100%;
    height: 56px;
    border: 1px solid #d1dfe5;
    border-radius: 14px;
    padding: 0 62px 0 18px;
    background: rgba(255, 255, 255, 0.98);
    color: var(--kk-text);
    font-size: 16px;
    line-height: 1;
    outline: none;
    box-shadow: 0 5px 18px rgba(31, 74, 94, 0.06);
    transition: border-color 150ms ease, box-shadow 150ms ease;
  }

  .kk-input::placeholder { color: #78878f; opacity: 1; }
  .kk-input:hover { border-color: #b9ced7; }
  .kk-input:focus {
    border-color: #66a9c8;
    box-shadow: 0 0 0 3px rgba(64, 145, 182, 0.12),
      0 6px 20px rgba(31, 74, 94, 0.07);
  }

  .kk-submit {
    position: absolute;
    right: 7px;
    top: 50%;
    width: 42px;
    height: 42px;
    transform: translateY(-50%);
    display: grid;
    place-items: center;
    padding: 0;
    border: 0;
    border-radius: 11px;
    color: #176f98;
    background: transparent;
    cursor: pointer;
  }

  .kk-submit:hover { color: #105c7f; background: #edf6fa; }
  .kk-submit:focus-visible,
  .kk-reset:focus-visible,
  .kk-link:focus-visible,
  .kk-select:focus-visible {
    outline: 3px solid rgba(64, 145, 182, 0.2);
    outline-offset: 2px;
  }

  .kk-submit svg {
    width: 22px;
    height: 22px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
  }

  .kk-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
    margin-top: 12px;
  }

  .kk-select,
  .kk-reset {
    min-width: 132px;
    height: 38px;
    border: 1px solid #cfdee4;
    border-radius: 999px;
    background: #fff;
    color: #26363e;
  }

  .kk-select {
    appearance: auto;
    padding: 0 13px;
    cursor: pointer;
  }

  .kk-reset {
    min-width: auto;
    padding: 0 15px;
    color: #62727a;
    cursor: pointer;
  }

  .kk-reset:hover { background: #f2f7f9; }
  .kk-results { margin-top: 22px; }

  .kk-heading {
    min-height: 39px;
    margin: 0 3px 12px;
    color: #687880;
    font-size: 13px;
  }

  .kk-heading strong {
    display: block;
    color: #1d2b33;
    font-size: 16px;
    font-weight: 800;
  }

  .kk-list { display: grid; gap: 13px; }

  .kk-card {
    display: grid;
    grid-template-columns: 176px minmax(0, 1fr);
    min-height: 164px;
    overflow: hidden;
    border: 1px solid var(--kk-border);
    border-radius: 16px;
    background: #fff;
    transition: transform 150ms ease, border-color 150ms ease,
      box-shadow 150ms ease;
  }

  .kk-card:hover {
    transform: translateY(-2px);
    border-color: #a9cbd9;
    box-shadow: 0 12px 30px rgba(30, 72, 92, 0.08);
  }

  .kk-image-wrap {
    position: relative;
    min-height: 164px;
    overflow: hidden;
    display: grid;
    place-items: center;
    border-right: 1px solid var(--kk-border);
    background: #eef4f6;
    color: #8cabb8;
    text-decoration: none;
    cursor: pointer;
  }

  .kk-image-wrap:hover { background: #e7f1f4; }
  .kk-image-wrap:focus-visible {
    outline: 3px solid rgba(64, 145, 182, 0.28);
    outline-offset: -3px;
  }

  .kk-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .kk-file-icon svg {
    width: 34px;
    height: 34px;
    fill: none;
    stroke: currentColor;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .kk-body {
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding: 19px 21px 17px;
  }

  .kk-breadcrumb {
    margin: 0 0 5px;
    overflow: hidden;
    color: #73838b;
    font-size: 12px;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .kk-title {
    margin: 0;
    color: var(--kk-text);
    font-size: 19px;
    line-height: 1.2;
    letter-spacing: -0.025em;
  }

  .kk-description {
    display: -webkit-box;
    margin: 8px 0 14px;
    overflow: hidden;
    color: #5e6d75;
    font-size: 14px;
    line-height: 1.5;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .kk-bottom {
    margin-top: auto;
    display: flex;
    gap: 15px;
    align-items: end;
    justify-content: space-between;
  }

  .kk-actions {
    display: flex;
    flex: 0 0 auto;
    min-width: 142px;
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
    justify-content: flex-start;
  }

  .kk-badges { display: flex; flex-wrap: wrap; gap: 5px; }
  .kk-badge {
    display: inline-flex;
    align-items: center;
    min-height: 22px;
    padding: 2px 9px;
    border-radius: 999px;
    background: #eef6f9;
    color: #35677c;
    font-size: 11px;
    white-space: nowrap;
  }

  .kk-link {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 38px;
    padding: 0 14px;
    border-radius: 11px;
    background: var(--kk-primary);
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    text-decoration: none;
  }

  .kk-link:hover { background: #11658d; }
  .kk-page-link {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 38px;
    padding: 0 13px;
    border: 1px solid #c8dbe3;
    border-radius: 11px;
    background: #f6fafb;
    color: #28657e;
    font-size: 13px;
    font-weight: 700;
    text-decoration: none;
  }

  .kk-page-link:hover { background: #eaf4f7; border-color: #a9cbd9; }
  .kk-actions .kk-page-link,
  .kk-actions .kk-link { width: 100%; }
  .kk-link svg {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  .kk-message {
    min-height: 190px;
    display: grid;
    place-items: center;
    padding: 30px;
    border: 1px dashed #c8d7dd;
    border-radius: 16px;
    background: #fff;
    color: #607078;
    text-align: center;
  }

  .kk-message strong {
    display: block;
    margin-bottom: 6px;
    color: #273740;
    font-size: 17px;
  }

  .kk-error { border-color: #e8c5c0; background: #fff8f7; color: #8f3c33; }

  @media (max-width: 760px) {
    .kk-shell { padding-top: 6px; }
    .kk-input { height: 52px; }
    .kk-submit { width: 38px; height: 38px; }
    .kk-select { flex: 1 1 calc(50% - 5px); min-width: 0; }
    .kk-card { grid-template-columns: 105px minmax(0, 1fr); min-height: 180px; }
    .kk-image-wrap { min-height: 180px; }
    .kk-body { padding: 15px 14px; }
    .kk-title { font-size: 17px; }
    .kk-description { -webkit-line-clamp: 3; }
    .kk-bottom { align-items: flex-start; flex-direction: column; }
    .kk-actions { width: 100%; }
    .kk-page-link, .kk-link { flex: 1 1 0; }
  }

  @media (max-width: 430px) {
    .kk-card { grid-template-columns: 1fr; }
    .kk-image-wrap {
      min-height: 128px;
      max-height: 150px;
      border-right: 0;
      border-bottom: 1px solid var(--kk-border);
    }
    .kk-description { -webkit-line-clamp: 2; }
  }
`;

function openCache(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(CACHE_DATABASE, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(CACHE_STORE)) {
        request.result.createObjectStore(CACHE_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Cachefout"));
  });
}

async function readCachedIndex(key: string): Promise<StaticSearchIndex | null> {
  if (!("indexedDB" in globalThis)) return null;
  try {
    const database = await openCache();
    const value = await new Promise<unknown>((resolve, reject) => {
      const transaction = database.transaction(CACHE_STORE, "readonly");
      const request = transaction.objectStore(CACHE_STORE).get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Cachefout"));
    });
    database.close();
    return isStaticSearchIndex(value) ? value : null;
  } catch {
    return null;
  }
}

async function writeCachedIndex(
  key: string,
  index: StaticSearchIndex
): Promise<void> {
  if (!("indexedDB" in globalThis)) return;
  try {
    const database = await openCache();
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(CACHE_STORE, "readwrite");
      transaction.objectStore(CACHE_STORE).put(index, key);
      transaction.oncomplete = () => resolve();
      transaction.onerror = () =>
        reject(transaction.error ?? new Error("Cachefout"));
    });
    database.close();
  } catch {
    // Een privémodus zonder IndexedDB mag de zoekfunctie niet blokkeren.
  }
}

async function loadIndex(
  indexUrl: string,
  onIndex: (index: StaticSearchIndex) => void
): Promise<StaticSearchIndex> {
  const cacheKey = `index:${indexUrl}`;
  const cached = await readCachedIndex(cacheKey);
  if (cached) onIndex(cached);

  const refresh = async (): Promise<StaticSearchIndex> => {
    const response = await fetch(indexUrl, {
      cache: "no-cache",
      credentials: "omit",
    });
    if (!response.ok) throw new Error(`Zoekindex gaf HTTP ${response.status}`);
    const payload: unknown = await response.json();
    if (!isStaticSearchIndex(payload)) {
      throw new Error("De zoekindex heeft een ongeldig formaat");
    }
    onIndex(payload);
    void writeCachedIndex(cacheKey, payload);
    return payload;
  };

  // Met een lokale reserve kan de eerste zoekopdracht meteen antwoorden. De
  // nieuwe index wordt op de achtergrond opgehaald en werkt de resultaten bij.
  if (cached) {
    void refresh().catch(() => undefined);
    return cached;
  }
  return refresh();
}

function requiredElement<T extends Element>(
  root: ParentNode,
  selector: string
): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Zoekonderdeel ontbreekt: ${selector}`);
  return element;
}

function appendSvg(target: HTMLElement, markup: string): void {
  const wrapper = document.createElement("span");
  wrapper.innerHTML = markup;
  const svg = wrapper.firstElementChild;
  if (svg) target.append(svg);
}

function createOption(value: string, label: string): HTMLOptionElement {
  const option = document.createElement("option");
  option.value = value;
  option.textContent = label;
  return option;
}

function fillSelect(
  select: HTMLSelectElement,
  values: string[],
  allLabel: string,
  prefix = ""
): void {
  const selected = select.value;
  select.replaceChildren(createOption("", allLabel));
  for (const value of values) {
    select.append(createOption(value, `${prefix}${value}`));
  }
  if (values.includes(selected)) select.value = selected;
}

function createBadge(label: string): HTMLSpanElement {
  const badge = document.createElement("span");
  badge.className = "kk-badge";
  badge.textContent = label;
  return badge;
}

function createResultCard(result: StaticSearchResult): HTMLElement {
  const card = document.createElement("article");
  card.className = "kk-card";

  const imageWrap = document.createElement("a");
  imageWrap.className = "kk-image-wrap";
  imageWrap.href = result.targetUrl || result.pageUrl;
  imageWrap.target = "_self";
  imageWrap.rel = "noopener";
  imageWrap.title = "Pagina bezoeken";
  imageWrap.setAttribute("aria-label", `Pagina bezoeken: ${result.title}`);
  const fallback = document.createElement("span");
  fallback.className = "kk-file-icon";
  appendSvg(fallback, FILE_ICON);
  imageWrap.append(fallback);

  if (result.imageUrl) {
    const image = document.createElement("img");
    image.className = "kk-image";
    image.src = result.imageUrl;
    image.alt = "";
    image.loading = "lazy";
    image.referrerPolicy = "no-referrer";
    image.addEventListener("error", () => image.remove(), { once: true });
    imageWrap.append(image);
  }

  const body = document.createElement("div");
  body.className = "kk-body";
  const breadcrumb = document.createElement("p");
  breadcrumb.className = "kk-breadcrumb";
  breadcrumb.textContent = result.breadcrumb;
  const title = document.createElement("h2");
  title.className = "kk-title";
  title.textContent = result.title;
  body.append(breadcrumb, title);

  if (result.description) {
    const description = document.createElement("p");
    description.className = "kk-description";
    description.textContent = result.description;
    body.append(description);
  }

  const bottom = document.createElement("div");
  bottom.className = "kk-bottom";
  const badges = document.createElement("div");
  badges.className = "kk-badges";
  const labels = [
    result.groupName ? `Groep ${result.groupName}` : "",
    result.subject,
    result.materialType,
    result.theme,
  ].filter(Boolean);
  for (const label of labels.slice(0, 4)) badges.append(createBadge(label));

  const actions = document.createElement("div");
  actions.className = "kk-actions";

  const pageLink = document.createElement("a");
  pageLink.className = result.downloadUrl ? "kk-page-link" : "kk-link";
  pageLink.href = result.targetUrl || result.pageUrl;
  pageLink.target = "_self";
  pageLink.rel = "noopener";
  pageLink.title = "Pagina bezoeken";
  pageLink.textContent = "Bekijken";
  appendSvg(pageLink, ARROW_ICON);
  actions.append(pageLink);

  if (result.downloadUrl) {
    const downloadLink = document.createElement("a");
    downloadLink.className = "kk-link";
    downloadLink.href = result.downloadUrl;
    downloadLink.target = "_self";
    downloadLink.rel = "noopener";
    downloadLink.textContent = "Downloaden";
    appendSvg(downloadLink, ARROW_ICON);
    actions.append(downloadLink);
  }

  bottom.append(badges, actions);
  body.append(bottom);
  card.append(imageWrap, body);
  return card;
}

function mountWidget(
  target: HTMLElement,
  loaderScript: HTMLScriptElement | null
): void {
  if (target.dataset.klasklaarMounted === "true") return;
  target.dataset.klasklaarMounted = "true";

  const shadow = target.shadowRoot ?? target.attachShadow({ mode: "open" });
  shadow.innerHTML = `
    <style>${STYLES}</style>
    <main class="kk-shell">
      <section aria-label="Zoeken in Klasklaar">
        <form class="kk-form" role="search">
          <div class="kk-input-wrap">
            <input class="kk-input" type="search" placeholder="Zoeken"
              aria-label="Zoeken" autocomplete="off">
            <button class="kk-submit" type="submit" aria-label="Zoeken">
              ${SEARCH_ICON}
            </button>
          </div>
        </form>
        <div class="kk-filters" aria-label="Zoekfilters" hidden>
          <select class="kk-select kk-group" aria-label="Groepen"></select>
          <select class="kk-select kk-subject" aria-label="Vakken"></select>
          <select class="kk-select kk-type" aria-label="Soorten"></select>
          <select class="kk-select kk-theme" aria-label="Thema's"></select>
          <button class="kk-reset" type="button" hidden>Filters wissen</button>
        </div>
      </section>
      <section class="kk-results" aria-live="polite" hidden>
        <div class="kk-heading"></div>
        <div class="kk-list"></div>
      </section>
    </main>`;

  const form = requiredElement<HTMLFormElement>(shadow, ".kk-form");
  const input = requiredElement<HTMLInputElement>(shadow, ".kk-input");
  const filters = requiredElement<HTMLElement>(shadow, ".kk-filters");
  const group = requiredElement<HTMLSelectElement>(shadow, ".kk-group");
  const subject = requiredElement<HTMLSelectElement>(shadow, ".kk-subject");
  const materialType = requiredElement<HTMLSelectElement>(shadow, ".kk-type");
  const theme = requiredElement<HTMLSelectElement>(shadow, ".kk-theme");
  const reset = requiredElement<HTMLButtonElement>(shadow, ".kk-reset");
  const resultsSection = requiredElement<HTMLElement>(shadow, ".kk-results");
  const heading = requiredElement<HTMLElement>(shadow, ".kk-heading");
  const list = requiredElement<HTMLElement>(shadow, ".kk-list");

  const baseUrl = loaderScript?.src || window.location.href;
  const indexUrl =
    loaderScript?.dataset.indexUrl ||
    new URL("search-index.json", baseUrl).toString();
  let currentIndex: StaticSearchIndex | null = null;
  let submittedQuery = "";
  let requestRevision = 0;
  let searched = false;
  let indexPromise: Promise<StaticSearchIndex> | null = null;

  const updateFacets = (facetsValue: SearchFacets): void => {
    fillSelect(group, facetsValue.groups, "Alle groepen", "Groep ");
    fillSelect(subject, facetsValue.subjects, "Alle vakken");
    fillSelect(materialType, facetsValue.types, "Alle soorten");
    fillSelect(theme, facetsValue.themes, "Alle thema's");
  };

  const showMessage = (
    titleText: string,
    description: string,
    error = false
  ): void => {
    const message = document.createElement("div");
    message.className = `kk-message${error ? " kk-error" : ""}`;
    const content = document.createElement("div");
    const strong = document.createElement("strong");
    strong.textContent = titleText;
    const text = document.createElement("span");
    text.textContent = description;
    content.append(strong, text);
    message.append(content);
    list.replaceChildren(message);
  };

  const renderResults = (
    results: StaticSearchResult[],
    total: number
  ): void => {
    heading.replaceChildren();
    const strong = document.createElement("strong");
    strong.textContent = `${total} ${total === 1 ? "resultaat" : "resultaten"}`;
    const description = document.createElement("span");
    description.textContent = `voor “${submittedQuery}”`;
    heading.append(strong, description);

    if (results.length === 0) {
      showMessage(
        "Nog niets gevonden",
        "Probeer een korter woord, haal een filter weg of zoek op een thema en groep."
      );
      return;
    }
    list.replaceChildren(...results.map(createResultCard));
  };

  const hasFilters = (): boolean =>
    Boolean(group.value || subject.value || materialType.value || theme.value);

  const runSearch = async (): Promise<void> => {
    if (!searched || !submittedQuery) return;
    const revision = ++requestRevision;
    resultsSection.hidden = false;
    filters.hidden = false;
    reset.hidden = !hasFilters();
    heading.innerHTML = "<strong>Even zoeken…</strong>";
    list.replaceChildren();

    try {
      const index = currentIndex ?? (await ensureIndex());
      await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
      if (revision !== requestRevision) return;
      const response = searchStaticIndex(index, {
        query: submittedQuery,
        group: group.value,
        subject: subject.value,
        materialType: materialType.value,
        theme: theme.value,
      });
      renderResults(response.results, response.total);
    } catch {
      if (revision !== requestRevision) return;
      heading.replaceChildren();
      showMessage(
        "Zoeken kon niet worden geladen",
        "Ververs de pagina. Een eerder geladen zoekindex wordt automatisch als reserve gebruikt.",
        true
      );
    }
  };

  const ensureIndex = (): Promise<StaticSearchIndex> => {
    indexPromise ??= loadIndex(indexUrl, (nextIndex) => {
      const changed = currentIndex?.generatedAt !== nextIndex.generatedAt;
      currentIndex = nextIndex;
      updateFacets(nextIndex.facets);
      if (changed && searched) void runSearch();
    }).catch((error: unknown) => {
      indexPromise = null;
      throw error;
    });
    return indexPromise;
  };

  // De homepage downloadt alleen het kleine widgetscript. De grotere index
  // wordt pas gevraagd zodra iemand de zoekbalk daadwerkelijk gebruikt.
  input.addEventListener(
    "focus",
    () => {
      void ensureIndex().catch(() => undefined);
    },
    { once: true }
  );

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    submittedQuery = input.value.trim();
    if (!submittedQuery) {
      searched = false;
      requestRevision += 1;
      filters.hidden = true;
      resultsSection.hidden = true;
      group.value = "";
      subject.value = "";
      materialType.value = "";
      theme.value = "";
      return;
    }
    searched = true;
    void runSearch();
  });

  for (const select of [group, subject, materialType, theme]) {
    select.addEventListener("change", () => {
      reset.hidden = !hasFilters();
      if (searched) void runSearch();
    });
  }

  reset.addEventListener("click", () => {
    group.value = "";
    subject.value = "";
    materialType.value = "";
    theme.value = "";
    reset.hidden = true;
    if (searched) void runSearch();
  });
}

const loaderScript = document.currentScript as HTMLScriptElement | null;

function start(): void {
  const targetId = loaderScript?.dataset.target || "klasklaar-zoeken";
  const target = document.getElementById(targetId);
  if (target) mountWidget(target, loaderScript);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", start, { once: true });
} else {
  start();
}
